import { getRouteApi } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import Button from '@/components/Button/Button'
import LinkText from '@/components/LinkText/LinkText'
import Table, { type TableAction } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import CommentListExpandedRow from '@/components/common/CommentListExpandedRow/CommentListExpandedRow'
import CommentModal from '@/components/common/CommentModal/CommentModal'
import ExportButton from '@/components/common/ExportButton'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import { RemoveCommentModal } from '@/components/common/RemoveCommentModal'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import useCommonCommentFilters from '@/hooks/useCommonCommentFilters'
import useCommonCommentsColumn from '@/hooks/useCommonCommentsColumn'
import { usePagination } from '@/hooks/usePagination'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import useCommentsDownload from '@/mutations/useCommentsDowload'
import useComments, { type Comment } from '@/queries/useComments'
import useCommentsCount from '@/queries/useCommentsCount'
import useSemesters from '@/queries/useSemesters'
import useStudentCoursesOptions from '@/queries/useStudentCoursesOptions'
import useStudentsOptions from '@/queries/useStudentOptions'
import useAuthStore from '@/store/useAuthStore'
import { checkIfFilterSelected } from '@/utils/check-if-filters-selected'
import { getFilterValue } from '@/utils/get-filter-value'

import { useColumns } from '../../../hooks/useColumns'
import { type CommentFiltersKeys } from '../constants/comment-filters'

const routeApi = getRouteApi(
  '/_auth/students-and-classes/classes/$classId/comments'
)

const CommentsView = () => {
  const { t } = useTranslation(['classes', 'common'])
  const { classId } = routeApi.useParams()
  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const { data: semesters, isLoading: isSemesterLoading } = useSemesters()
  const user = useAuthStore(store => store.user)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isOpenRemoveCommentModal, setIsOpenRemoveCommentModal] =
    useState(false)

  const { isMobile } = useScreenResolution()

  const {
    data: comments,
    isLoading: isCommentsLoading,
    refetch: refetchComments
  } = useComments({
    classId,
    ...search
  })

  const {
    data: commentsCount,
    isLoading: isCommentsCountLoading,
    refetch: refetchCommentsCount
  } = useCommentsCount({
    semester: search.semester,
    classId
  })

  const refetchAllComments = () => {
    refetchComments()
    refetchCommentsCount()
    refetchAddedByOptions()
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setCurrentComment(null)
  }

  const { data: courses, isLoading: isCoursesLoading } =
    useStudentCoursesOptions({
      classId,
      semester: search.semester
    })

  const { data: students, isLoading: isStudentsLoading } = useStudentsOptions({
    classId: [classId],
    semester: search.semester
  })

  const pagination = usePagination(routeApi)

  const handleSemesterChange = (value: string) => {
    navigate({
      search: {
        semester: value,
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const { commonCommentsColumns } = useCommonCommentsColumn()

  const { columns, ...manageColumns } = useColumns<Comment>(
    [
      {
        key: 'student',
        label: t('header.student'),
        width: isMobile ? TABLE_CELL_WIDTH.XL : undefined,
        render: (value: Comment) => (
          <LinkText
            to="/students-and-classes/students/$studentId/details"
            params={{
              studentId: value.student.id
            }}
          >
            {value.student.name}
          </LinkText>
        )
      },
      {
        key: 'source',
        label: t('header.source'),
        width: isMobile ? TABLE_CELL_WIDTH.LG : undefined,
        render: (value: Comment) =>
          value.group?.course.displayName ?? t('text.other')
      },
      ...commonCommentsColumns
    ],
    { routeApi }
  )

  const handleChangeFilter = (
    key: CommentFiltersKeys,
    value?: string | string[] | Date
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const handleClearAll = () => {
    navigate({
      search: {
        semester: semesters?.currentSemester.id || '',
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const { commonCommentsFilters, refetchAddedByOptions } =
    useCommonCommentFilters({
      ...search,
      semester: semesters?.getSemester(search.semester),
      classId,
      onChangeFilter: handleChangeFilter
    })

  const filters: Filter[] = [
    {
      label: t('label.semester'),
      variant: 'semester',
      size: 'wide',
      filterProps: {
        id: 'semester',
        loading: isSemesterLoading,
        options: semesters?.semestersFromCurrentYearOptions || [],
        semesterList: semesters?.list || [],
        value: search.semester,
        onChange: handleSemesterChange
      }
    },
    {
      label: t('label.student'),
      variant: 'multiselect',
      size: 'wide',
      filterProps: {
        id: 'student',
        multiple: true,
        loading: isStudentsLoading,
        options: students || [],
        value: search.studentsId,
        placeholder: t('label.student'),
        onChange: value => handleChangeFilter('studentsId', value)
      }
    },
    {
      label: t('label.course'),
      variant: 'multiselect',
      filterProps: {
        id: 'course',
        multiple: true,
        loading: isCoursesLoading,
        options: courses?.options || [],
        value: search.coursesId,
        placeholder: t('label.course'),
        onChange: value => handleChangeFilter('coursesId', value)
      }
    },
    ...commonCommentsFilters
  ]

  const isSomeFilterSelected = checkIfFilterSelected(filters, ['semester'])

  const [currentComment, setCurrentComment] = useState<Comment | null>(null)

  const selectedSemester = semesters?.getSemester(search.semester)

  const canChangeComments = selectedSemester?.status !== 'archived'

  const actions: (row: Comment) => TableAction<Comment>[] = row =>
    canChangeComments && user?.isSuperAdmin
      ? [
          {
            icon: <EditIcon />,
            text: t('button.edit'),
            hidden: row.addedBy.id !== user?.id,
            onClick: (item: Comment) => {
              setCurrentComment(item)
              setIsModalOpen(true)
            }
          },
          {
            icon: <DeleteIcon />,
            text: t('button.remove'),
            variantAction: 'danger' as const,
            hidden: row.addedBy.id !== user?.id,
            onClick: (item: Comment) => {
              setCurrentComment(item)
              setIsOpenRemoveCommentModal(true)
            }
          }
        ].filter(item => !item.hidden)
      : []

  const download = useCommentsDownload({
    ...search,
    classId,
    columnsKey: manageColumns.activeColumnKeys
  })
  return (
    <>
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
        actionsRender={
          <>
            <ExportButton {...download} />

            <CommentModal
              semester={search.semester}
              open={isModalOpen}
              onOpenChange={value => {
                if (value) setIsModalOpen(value)
                else closeModal()
              }}
              classId={classId}
              onUpdate={refetchAllComments}
              commentId={currentComment?.id}
              trigger={
                <Button icon={<AddIcon />} disabled={!canChangeComments}>
                  {t('common:button.add-comment')}
                </Button>
              }
            />
          </>
        }
        manageColumns={manageColumns}
      />
      <Table
        id="comments-table"
        data={comments?.list || []}
        isLoading={isCommentsLoading || isCommentsCountLoading}
        columns={columns}
        count={comments?.count}
        totalCount={commentsCount}
        {...pagination}
        actions={actions}
        expandedRowRender={record => (
          <CommentListExpandedRow comment={record} />
        )}
        caption={t('header.comments')}
      />

      {currentComment ? (
        <RemoveCommentModal
          open={isOpenRemoveCommentModal}
          onOpenChange={setIsOpenRemoveCommentModal}
          commenId={currentComment.id}
          onSuccess={refetchAllComments}
        />
      ) : null}
    </>
  )
}

export default CommentsView
